/**
 * Political Map Constants
 *
 * Defines the quadrant names and sub-labels for the 2D political map visualization.
 * The map projects 7D ideology onto a 3x3 grid with 9 main quadrants,
 * each containing a 3x3 sub-grid for more granular positioning.
 */

// ============================================================================
// Axis Labels
// ============================================================================

export const POLITICAL_MAP_AXES = {
  horizontal: ['Left', 'Center', 'Right'] as const,
  vertical: ['Pluralist', 'Institutional', 'Centralized'] as const  // bottom to top
} as const

// ============================================================================
// Quadrant Names (3x3 main grid)
// ============================================================================

export const POLITICAL_MAP_QUADRANTS = {
  // [verticalIndex][horizontalIndex] = quadrant name
  // Row 0 = Pluralist (bottom), Row 2 = Centralized (top)
  quadrantNames: [
    // Pluralist row (bottom, y index 0)
    ['Participatory Progressives', 'Civil Libertarians', 'Market Liberties'],
    // Institutional row (middle, y index 1)
    ['Social Democrats', 'Pluralist Centrists', 'Liberal Conservatives'],
    // Centralized row (top, y index 2)
    ['State Populists', 'Managed Democrats', 'National Conservatives']
  ] as const,

  // Sub-labels within each quadrant [quadrantName][subY][subX]
  // Each 3x3 sub-grid provides granular positioning within the quadrant
  // subY: 0 = bottom of quadrant, 2 = top of quadrant
  // subX: 0 = left of quadrant, 2 = right of quadrant
  subLabels: {
    // ========================================================================
    // Top Row (Centralized)
    // ========================================================================
    'State Populists': [
      // subY 0 (lower part of quadrant - toward Institutional)
      ['Democratic Redistributionists', 'Interventionist Democrats', 'State-Capital Reformers'],
      // subY 1 (middle)
      ['Movement Socialists', 'Populist Institutionalists', 'Developmental Statists'],
      // subY 2 (upper part - most Centralized)
      ['Redistributive Strongmen', 'National Welfare Leaders', 'Left-National Authoritarians']
    ],
    'Managed Democrats': [
      ['Institutional Stabilizers', 'Regulatory Moderates', 'Market-Coordinating Democrats'],
      ['Administrative Centrists', 'Managed Pluralists', 'Development Managers'],
      ['Technocratic Executives', 'Stability Presidents', 'Executive Reformers']
    ],
    'National Conservatives': [
      ['Law-and-Order Democrats', 'Traditional Reformers', 'Executive Market Guardians'],
      ['National Party Conservatives', 'Cultural Statists', 'Strategic Capitalists'],
      ['Order-First Leaders', 'Patriotic Executives', 'Security Capitalists']
    ],

    // ========================================================================
    // Middle Row (Institutional)
    // ========================================================================
    'Social Democrats': [
      // subY 0 (lower - toward Pluralist)
      ['Civic Social Progressives', 'Participatory Reformers', 'Local Welfare Liberals'],
      // subY 1 (middle)
      ['Mainline Social Democrats', 'Coalition Progressives', 'Pragmatic Redistributionists'],
      // subY 2 (upper - toward Centralized)
      ['Welfare Institutionalists', 'Labor Constitutionalists', 'Equality Reformists']
    ],
    'Pluralist Centrists': [
      ['Civic Consensus Builders', 'Deliberative Democrats', 'Local Institutionalists'],
      ['Pragmatic Centrists', 'Coalition Managers', 'Incremental Reformers'],
      ['Constitutional Technocrats', 'Rule-of-Law Moderates', 'Institutional Balancers']
    ],
    'Liberal Conservatives': [
      ['Civic Market Moderates', 'Federal Conservatives', 'Local Enterprise Democrats'],
      ['Mainline Conservatives', 'Reform Conservatives', 'Coalition Capitalists'],
      ['Constitutional Conservatives', 'Fiscal Legalists', 'Market Institutionalists']
    ],

    // ========================================================================
    // Bottom Row (Pluralist)
    // ========================================================================
    'Participatory Progressives': [
      // subY 0 (lowest - most Pluralist)
      ['Mutualist Democrats', 'Commons Organizers', 'Local Solidarity Builders'],
      // subY 1 (middle)
      ['Participatory Social Democrats', 'Green Progressives', 'Municipal Reformers'],
      // subY 2 (upper - toward Institutional)
      ['Grassroots Socialists', 'Cooperative Reformers', 'Community Egalitarians']
    ],
    'Civil Libertarians': [
      ['Free Association Democrats', 'Local Autonomy Liberals', 'Radical Pluralists'],
      ['Pluralist Liberals', 'Institutional Libertarians', 'Civil Market Moderates'],
      ['Rights Maximalists', 'Constitutional Libertarians', 'Civil Process Liberals']
    ],
    'Market Liberties': [
      ['Enterprise Localists', 'Voluntary Exchange Democrats', 'Community Free-Market Advocates'],
      ['Classical Liberals', 'Federal Conservatives', 'Market Reformists'],
      ['Constitutional Market Liberals', 'Rights-First Capitalists', 'Legal Federalists']
    ]
  } as const
} as const

// ============================================================================
// Type Definitions
// ============================================================================

export type QuadrantName = typeof POLITICAL_MAP_QUADRANTS.quadrantNames[number][number]
export type HorizontalLabel = typeof POLITICAL_MAP_AXES.horizontal[number]
export type VerticalLabel = typeof POLITICAL_MAP_AXES.vertical[number]

// ============================================================================
// Quadrant Color Mapping (for visual styling)
// ============================================================================

export const QUADRANT_COLORS = {
  // Left column - red tones
  'State Populists': { bg: 'bg-red-900/20', border: 'border-red-700/30', text: 'text-red-300' },
  'Social Democrats': { bg: 'bg-red-800/20', border: 'border-red-600/30', text: 'text-red-200' },
  'Participatory Progressives': { bg: 'bg-red-700/20', border: 'border-red-500/30', text: 'text-red-100' },

  // Center column - purple/neutral tones
  'Managed Democrats': { bg: 'bg-purple-900/20', border: 'border-purple-700/30', text: 'text-purple-300' },
  'Pluralist Centrists': { bg: 'bg-slate-700/20', border: 'border-slate-500/30', text: 'text-slate-200' },
  'Civil Libertarians': { bg: 'bg-purple-700/20', border: 'border-purple-500/30', text: 'text-purple-100' },

  // Right column - blue tones
  'National Conservatives': { bg: 'bg-blue-900/20', border: 'border-blue-700/30', text: 'text-blue-300' },
  'Liberal Conservatives': { bg: 'bg-blue-800/20', border: 'border-blue-600/30', text: 'text-blue-200' },
  'Market Liberties': { bg: 'bg-blue-700/20', border: 'border-blue-500/30', text: 'text-blue-100' }
} as const

// ============================================================================
// Quadrant Descriptions (for tooltips and detail views)
// ============================================================================

export const QUADRANT_DESCRIPTIONS: Record<QuadrantName, string> = {
  // Top row (Centralized)
  'State Populists':
    'Favor strong government intervention in the economy combined with centralized political authority. Often emphasize national welfare programs and state-led development.',
  'Managed Democrats':
    'Support technocratic governance with strong institutions managing both economy and society. Emphasize stability, expertise, and orderly reform.',
  'National Conservatives':
    'Combine free market economics with strong central authority focused on national security, traditional values, and order maintenance.',

  // Middle row (Institutional)
  'Social Democrats':
    'Advocate for robust welfare states and economic regulation within democratic institutions. Balance market economics with social protection.',
  'Pluralist Centrists':
    'Favor balanced governance with multiple centers of power. Pragmatic approach combining market mechanisms with moderate regulation.',
  'Liberal Conservatives':
    'Support free markets and limited government within strong institutional frameworks. Emphasize rule of law and gradual reform.',

  // Bottom row (Pluralist)
  'Participatory Progressives':
    'Emphasize grassroots democracy, cooperative economics, and decentralized decision-making. Favor community control over centralized authority.',
  'Civil Libertarians':
    'Prioritize individual rights and freedoms with minimal government intervention in personal choices. Favor decentralized power structures.',
  'Market Liberties':
    'Combine free market economics with decentralized governance. Emphasize voluntary exchange, local autonomy, and limited state power.'
}

// ============================================================================
// Quadrant Icons (emoji representations)
// ============================================================================

export const QUADRANT_ICONS: Record<QuadrantName, string> = {
  // Top row (Centralized)
  'State Populists': '🏭',
  'Managed Democrats': '🏛️',
  'National Conservatives': '🦅',

  // Middle row (Institutional)
  'Social Democrats': '⚖️',
  'Pluralist Centrists': '🤝',
  'Liberal Conservatives': '📜',

  // Bottom row (Pluralist)
  'Participatory Progressives': '✊',
  'Civil Libertarians': '🗽',
  'Market Liberties': '💱'
}
